/**
 * Recipes-tab surface for PROD-225: chef-initiated component substitution
 * limited to pre-approved alternates.
 *
 * Deliberately shaped like `IngredientBrandPicker` (the ingredient-level
 * dropdown on the same page): a bordered chip under the row name that opens
 * a popover (bottom drawer on mobile) listing the choices, current one
 * checked. The chip renders ONLY when at least one pre-approved alternate
 * exists — no alternates, no affordance (mirrors `brandControlMode`
 * requiring a real choice before showing a control).
 *
 * Same contract as the shipment-drawer control this replaces: the server
 * derives the eligible list from ACTIVE + ORDO_APPROVED
 * `MenuSubstitutionRule` rows whose scope reaches the shipment — there is
 * no second notion of "approved" — and the swap is ORDER-LOCAL (rewrites
 * the shipments' lines; the menu itself is untouched). A day row aggregates
 * every shipment carrying the component (batch prep view), so picking an
 * alternate applies to EACH of the row's shipments; the server re-validates
 * rule scope per shipment and a shipment the rule doesn't reach fails alone
 * (409) — the toast reports the split rather than pretending the batch
 * succeeded.
 */

import { cn } from '@ordo/ui/cn'
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from '@ordo/ui/drawer'
import { Popover, PopoverContent, PopoverTrigger } from '@ordo/ui/popover'
import { toast } from '@ordo/ui/sonner'
import { Check, ChevronDown } from 'lucide-react'
import { type ComponentPropsWithRef, useState } from 'react'

import {
  type ChefSubstituteOption,
  useApplySubstitutionMutation,
  useEligibleSubstitutesQuery,
} from '@/api/kitchen-orders'
import { useIsMobile } from '@/lib/use-is-mobile'

/** Same finger-sized row treatment as the brand picker's option rows. */
const ROW = 'flex min-h-11 w-full items-center justify-between gap-2 rounded-md px-3 text-left text-sm hover:bg-muted'

export function RecipeSwapMenu({
  shipmentIds,
  componentId,
  componentName,
}: {
  /** The day's non-cancelled shipments carrying this component. */
  shipmentIds: string[]
  componentId: string
  componentName: string
}) {
  const isMobile = useIsMobile()
  const [open, setOpen] = useState(false)
  // Eager fetch: the chip only exists when an alternate does, so eligibility
  // must be known before any click. Eligibility is listed off the first
  // shipment; per-shipment differences surface at apply time via the
  // server's per-shipment re-validation.
  const substitutesQuery = useEligibleSubstitutesQuery(
    { shipmentId: shipmentIds[0] ?? '', componentId },
    { enabled: shipmentIds.length > 0 },
  )
  const applyMutation = useApplySubstitutionMutation()
  const [applying, setApplying] = useState(false)

  const substitutes = substitutesQuery.data?.substitutes ?? []
  if (shipmentIds.length === 0 || substitutes.length === 0) {
    // No pre-approved alternate → no dropdown (nothing to choose).
    return null
  }

  async function applyToAll(sub: ChefSubstituteOption) {
    setApplying(true)
    let succeeded = 0
    let failed = 0
    for (const shipmentId of shipmentIds) {
      try {
        await applyMutation.mutateAsync({ shipmentId, componentId, ruleId: sub.ruleId })
        succeeded += 1
      } catch {
        failed += 1
      }
    }
    setApplying(false)
    setOpen(false)
    if (failed === 0) {
      toast.success(
        shipmentIds.length === 1
          ? `Swapped ${componentName} → ${sub.replacementName} for this order.`
          : `Swapped ${componentName} → ${sub.replacementName} across ${succeeded} orders.`,
      )
    } else if (succeeded === 0) {
      toast.error(`Could not swap ${componentName} — no order accepted the substitution.`)
    } else {
      toast.warning(
        `Swapped ${componentName} → ${sub.replacementName} for ${succeeded} of ${shipmentIds.length} orders — ` +
          `the approval doesn't cover the rest.`,
      )
    }
  }

  const body = (
    <div className="flex flex-col gap-1 p-1">
      <p className="px-3 py-1.5 text-xs text-muted-foreground">
        Which do you make for {componentName}? Applies to every order this day.
      </p>
      {/* The current component leads the list, checked — same "your current
          choice is one of the options" shape as the brand picker. */}
      <button type="button" disabled className={cn(ROW, 'disabled:opacity-100')}>
        <span className="py-2">
          {componentName}
          <span className="text-muted-foreground"> (on the menu)</span>
        </span>
        <Check className="h-4 w-4 shrink-0 text-primary" strokeWidth={2} />
      </button>
      {substitutes.map((sub) => (
        <button
          key={sub.ruleId}
          type="button"
          disabled={applying}
          onClick={() => void applyToAll(sub)}
          className={cn(ROW, 'disabled:opacity-50')}>
          <span className="py-2">
            {sub.replacementName}
            {sub.replacementServingSizeLabel ? (
              <span className="text-muted-foreground"> — {sub.replacementServingSizeLabel}</span>
            ) : null}
          </span>
        </button>
      ))}
    </div>
  )

  if (isMobile) {
    return (
      <>
        <SwapTrigger label={componentName} onClick={() => setOpen(true)} />
        <Drawer open={open} onOpenChange={setOpen}>
          <DrawerContent>
            <DrawerHeader className="pb-0">
              <DrawerTitle className="text-base">{componentName}</DrawerTitle>
            </DrawerHeader>
            <div className="max-h-[60vh] overflow-y-auto pb-4">{body}</div>
          </DrawerContent>
        </Drawer>
      </>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <SwapTrigger label={componentName} />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-0">
        {body}
      </PopoverContent>
    </Popover>
  )
}

/**
 * Same chip affordance as the brand picker's `BrandTrigger` — and the same
 * rule: spread the rest props (and the React 19 ref-as-prop) onto the
 * button, because inside `<PopoverTrigger asChild>` Radix's Slot-injected
 * ref is what anchors the popover.
 */
function SwapTrigger({ label, className, ...props }: { label: string } & ComponentPropsWithRef<'button'>) {
  return (
    <button
      type="button"
      {...props}
      aria-label={`Swap ${label}`}
      className={cn(
        'inline-flex min-h-8 max-w-full items-center gap-1 rounded-md border border-border px-2 py-0.5',
        'text-xs text-muted-foreground hover:bg-muted hover:text-foreground',
        className,
      )}>
      <span className="truncate">{label}</span>
      <ChevronDown className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
    </button>
  )
}
